import React, {useEffect, useState} from "react";
import Head from "next/head";
import styled from "styled-components";

import SafeLink from "components/commons/SafeLink";
import {WealthyValidations, handleApiError, triggerToast, WealthyEnv} from "helpers";
import {subscribeToNewsletter} from "api";
import useRecaptcha from "hooks/useRecaptcha";

const MoneyOrder = ({moneyOrderData = {}}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const {token, refreshToken} = useRecaptcha("homepage", 1000 * 60 * 1.5, WealthyEnv.RECAPTCHA_TOKEN);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const subscribeToMoneyOrder = async (event) => {
    event.preventDefault();
    const isEmailValid = WealthyValidations.validateFormField.email(email);
    if (!isEmailValid) {
      triggerToast("Please enter a valid email address", "error");
      return;
    }
    try {
      setIsLoading(true);
      const freshToken = token || await refreshToken();
      if (!freshToken) {
        triggerToast("Captcha failed, please refresh the page and try again", "error");
        return;
      }
      const res = await subscribeToNewsletter({email, token: freshToken});
      if (res.status === 200) {
        triggerToast("Subscribed to Money Order", "success");
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  return (
    <>
      <Head>
        <script
          defer
          src={`https://www.google.com/recaptcha/api.js?render=${WealthyEnv.RECAPTCHA_TOKEN}`}
        />
        <script
          defer
          src={`https://www.google.com/recaptcha/api.js?render=${WealthyEnv.RECAPTCHA_SITEKEY}`}
        />
      </Head>
      <Section>
        <BackgroundGrid>
        <PaperPlaneLeft
          alt="paper-plane-asset"
          src={moneyOrderData.planeImage}
          width={280}
          height={281}
          loading="lazy"
          decoding="async"
        />

        <Card>
          <PaperPlaneMobile
            alt="paper-plane-asset"
            src={moneyOrderData.planeImage}
            width={280}
            height={281}
            loading="lazy"
            decoding="async"
          />

          <Title>{moneyOrderData.title}</Title>
          <Subtitle>
            {moneyOrderData.subtitle}
          </Subtitle>
          <Description>
            {moneyOrderData.descriptionItems && moneyOrderData.descriptionItems.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </Description>

          {isMobile ? (

            <MobileFormContainer>
              <MobileEmailInput placeholder="Enter your Email Address" onChange={handleEmailChange} />
              <MobileSubscribeButton disabled={isLoading} onClick={subscribeToMoneyOrder}>{isLoading ? "Subscribing..." : "Subscribe"}</MobileSubscribeButton>
            </MobileFormContainer>
          ) : (
            <FormRow>
              <EmailInput placeholder="Enter your Email Address" onChange={handleEmailChange} />
              <SubscribeButton disabled={isLoading || !email} onClick={subscribeToMoneyOrder}>{isLoading ? "Subscribing..." : "Subscribe"}</SubscribeButton>
            </FormRow>
          )}

          <SpotifyRow>
            <SpotifyLink
              href={moneyOrderData.spotifyLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <SpotifyIcon
                alt="spotify-image"
                src={moneyOrderData.spotifyImage}
                width={72}
                height={72}
                loading="lazy"
                decoding="async"
              />
              <SpotifyText>Listen on Spotify</SpotifyText>
            </SpotifyLink>
          </SpotifyRow>
        </Card>
        </BackgroundGrid>
      </Section>
    </>
  );
};

export default MoneyOrder;

const Section = styled.section`
  width: 100vw;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0 0 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 52px 0 0 0;
}

`;

const BackgroundGrid = styled.div`
  width: 85%;
  max-width: 125rem;
  min-height: 42rem;
  position: relative;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    width: 85%;
    max-width: 125rem;
    margin: 0 auto; 
  }
`;

const Card = styled.div`
  background: #faf8f6;
  border-radius: 28px;
  padding: 48px 32px 36px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 32rem;
  width: 100%;
  margin: 10rem 0;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 24px 16px;
    width: 100%;
    margin-top: -1.34rem;
    margin-bottom: 10rem;
  }
`;

const Title = styled.h2`
  font-family: 'DM Serif Display';
  font-size: 5rem;
  font-weight: 400;
  color: #1C1C1C;
  margin: 0 0 12px 0;
  text-align: center;

    @media (max-width: 768px) {
    text-align: left;
    font-size: 2.3rem;
    width: 100%;
  }
`;

const Subtitle = styled.div`
  color: #7E7E7E;
  font-family: 'Lato';
  font-weight: 400;
  font-size: 1.6rem;
  margin-bottom: 1.8rem;
  text-align: center;

    @media (max-width: 768px) {
    text-align: left;
    font-size: 1.33rem;
    width: 70%;
  }
`;

const Description = styled.ul`
  color: #1C1C1CE7;
  font-family: 'Lato';
  font-size: 1.6rem;
  font-weight: 400;
  text-align: center;
  margin-bottom: 3.2rem;
  line-height: 1.5rem;
  width: 60%;
  padding-left: 0;
  list-style-type: none;


  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 8px;

  li {
    margin-bottom: 8px;
  }


  li:not(:last-child)::after {
    content: "•";
    margin-left: 8px;
  }


  @media (max-width: 768px) {
    display: block;
    font-size: 1.33rem;
    text-align: left;
    list-style-type: disc;
    list-style-position: inside;
    width: 80%;


    li:not(:last-child)::after {
      content: "";
    }
  }
`;

const FormRow = styled.form`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 48px;
  padding: 8px 8px 8px 32px;
  width: 48rem;
  max-width: 90vw;
  margin: 0 auto 18px auto;

  @media (max-width: 768px) {
    flex-direction: column; 
    align-items: stretch;   
    padding: 6px 6px 6px 16px;
    width: 100%;
    max-width: 100%;
    border-radius: 48px;
    gap: 0;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  border: none;
  font-family: 'Lato';
  font-weight: 400;
  outline: none;
  font-size: 1.6rem;
  padding: 16px 0;
  border-radius: 48px;
  background: transparent;
  color: #7E7E7E;

  &::placeholder {
    color: #bbb;
    font-size: 1.6rem;
  }

  @media (max-width: 768px) {
    padding: 12px 0;
    font-size: 1.33rem;
    text-align: center;
    margin: auto;
  }
`;

const SubscribeButton = styled.button`
  background: #6725F4;
  font-family: 'Lato';
  color: #fff;
  border: none;
  border-radius: 48px;
  width: 14rem;
  height: 5rem;
  font-size: 1.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #6725F4;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 10px;   /* Add spacing from input */
    width: 100%;        /* Full width */
    height: 5.2rem;
    font-size: 1.333rem;
  }
`;

const SpotifyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SpotifyIcon = styled.img`
  width: 3.6rem;
  height: 3.6rem;
  display: inline-block;

  @media (max-width: 768px) {
    width: 3rem;
    height:3rem;
  }
`;

const SpotifyLink = styled(SafeLink)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;


const SpotifyText = styled.p`
  color: #7E7E7E;
  font-family: 'Lato';
  font-size: 1.33rem;
  font-weight: 400;
`;

const PaperPlaneLeft = styled.img`
  position: absolute;
  width: 13rem;
  height: 11rem;
  left: 15rem;
  top: 13rem;
  transform: translate(-50%, -50%);
  z-index: 3;

  @media (max-width: 768px) {
    width: 90px;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-bottom: 18px;
`;

const MobileEmailInput = styled.input`
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 48px;
  font-family: 'Lato';
  outline: none;
  text-align: center;
  font-size: 1.6rem;
  padding: 16px 24px;
  background: #fff;
  color: #7E7E7E;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);

  &::placeholder {
    color: #bbb;
    font-size: 1.6rem;
  }
`;

const MobileSubscribeButton = styled.button`
  background: #6725F4;
  font-family: 'Lato';
  color: #fff;
  border: none;
  border-radius: 48px;
  width: 100%;
  height: 5.2rem;
  font-size: 1.333rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 2px 8px rgba(124,58,237,0.08);

  &:hover {
    background: #6725F4;
  }
`;

const PaperPlaneMobile = styled.img`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    width: 25%;
    height: 25%;
    right: -5px;
    top: 4px;
    transform: translate(-50%, -50%);
    z-index: 3;
  }
`;